const PROFILE_REFRESH_MS = 15_000;

interface BrowserLifecycleHooks {
  resume(): void;
  refreshProfile(): void;
  unlockAudio(): void;
  suspendAudio(hidden: boolean): void;
  navigateSound(): void;
}

/** App-lifetime browser events; screens never install reconnect or eligibility loops. */
export function installBrowserLifecycle(hooks: BrowserLifecycleHooks): () => void {
  let pageHidden = false;
  const recover = () => {
    const hidden = pageHidden || document.visibilityState !== 'visible';
    hooks.suspendAudio(hidden);
    if (!hidden) hooks.resume();
  };
  const pageHide = () => {
    pageHidden = true;
    hooks.suspendAudio(true);
  };
  const pageShow = () => {
    pageHidden = false;
    recover();
  };
  const pointerDown = (event: PointerEvent) => {
    // Touch/pen activation arrives on pointerup, not pointerdown. Don't spend the
    // one-shot post-background recovery before Safari accepts the gesture.
    if (event.pointerType === 'mouse') unlock();
  };
  const unlock = () => {
    if (!pageHidden && document.visibilityState === 'visible') hooks.unlockAudio();
  };
  const uiSound = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target.closest('button, a') : null;
    if (target && !target.hasAttribute('disabled') && !target.hasAttribute('data-no-ui-sound'))
      hooks.navigateSound();
  };
  // New blocks and early removals become visible in an open, idle tab within 15 seconds.
  const profileRefresh = window.setInterval(hooks.refreshProfile, PROFILE_REFRESH_MS);
  hooks.suspendAudio(document.visibilityState !== 'visible');
  window.addEventListener('online', recover);
  window.addEventListener('pagehide', pageHide);
  window.addEventListener('pageshow', pageShow);
  document.addEventListener('visibilitychange', recover);
  document.addEventListener('pointerdown', pointerDown, { passive: true });
  document.addEventListener('pointerup', unlock, { passive: true });
  document.addEventListener('keydown', unlock);
  document.addEventListener('click', uiSound);
  return () => {
    window.clearInterval(profileRefresh);
    window.removeEventListener('online', recover);
    window.removeEventListener('pagehide', pageHide);
    window.removeEventListener('pageshow', pageShow);
    document.removeEventListener('visibilitychange', recover);
    document.removeEventListener('pointerdown', pointerDown);
    document.removeEventListener('pointerup', unlock);
    document.removeEventListener('keydown', unlock);
    document.removeEventListener('click', uiSound);
  };
}
